import dayjs from "dayjs"
import {
  ElecRates,
  TariffType,
  RateType,
  ElecProductType,
  SO_GREEN_TRACKER_TARIFF_CODE
} from "../constants/tariff"

class TariffQuote {
  constructor(rawQuote) {
    this.quoteId = String(rawQuote.quoteId)
    this.fullTariffName = rawQuote.tariffName
    this.tariffName = rawQuote.shortTariffName.split("/")[0]
    this.rate = rawQuote.rate
    this.durationMonths = rawQuote.tariffLengthInMonths
    this.paymentMethod =
      rawQuote.payment === "Monthly Direct Debit"
        ? "Direct Debit"
        : rawQuote.payment
    this.fuelType = rawQuote.fuelType

    this.annualCostValue = rawQuote.costPerYearInPounds
    this.monthlyCostValue = rawQuote.costPerMonthInPounds
    this.annualSavings = rawQuote.savingPerYearInPounds
    this.elecProductType = rawQuote.elecProductType
    this.tariffType = rawQuote.tariffType

    this.elecRates = rawQuote.elecRates
    this.elecStandingCharge = rawQuote.elecStandingChargeInPence

    this.gasUnitRate = rawQuote.gasCostPerKwhInPence
    this.gasStandingCharge = rawQuote.gasStandingChargeInPence
    this.annualGasUsage = rawQuote.gasConsumptionPerYearInKwh
    this.annualElecUsage = rawQuote.elecConsumptionPerYearInKwh

    this.elecExitFee = rawQuote.elecExitFeeInPounds
    this.gasExitFee = rawQuote.gasExitFeeInPounds
    this.elecVatRate = rawQuote.elecVatRate
    this.gasVatRate = rawQuote.gasVatRate
    this.startDate = dayjs(rawQuote.supplyStartDate)
    this.firstDdDate = rawQuote.firstDdDate
    this.otherDetails = rawQuote.otherDetails
    this.seasonalAdjustmentFactor = rawQuote.seasonalAdjustmentFactor / 100
    this.currentReferralFee = rawQuote.currentReferralFee
    this.elecProductCode = rawQuote.elecProductCode
    this.gasProductCode = rawQuote.gasProductCode
    this.isRecommended = rawQuote.isRecommended
  }

  get hasDualFuel() {
    return this.fuelType === "Dual Fuel"
  }

  get hasGas() {
    return this.hasDualFuel || this.fuelType === "Gas"
  }

  get hasElec() {
    return this.hasDualFuel || this.fuelType === "Electricity"
  }

  get isVariable() {
    return this.rate === RateType.Variable || !this.durationMonths
  }

  get isEco7() {
    return this.elecProductType === ElecProductType.Eco7
  }

  get isTouProduct() {
    return this.elecProductType === ElecProductType.TOU
  }

  get isEvTariff() {
    return this.tariffType === TariffType.EV
  }

  monthlyCostSummer() {
    return this.getAdjustedMonthlyCost(1 - this.seasonalAdjustmentFactor)
  }

  monthlyCostWinter() {
    return this.getAdjustedMonthlyCost(1 + this.seasonalAdjustmentFactor)
  }

  monthlyCost() {
    return this.monthlyCostValue
  }

  annualCostAdjusted() {
    return this.annualCostValue
  }

  get elecStandardRate() {
    return this.elecRates.find(rate => rate.name === ElecRates.Standard)
  }

  get elecDayRate() {
    return this.elecRates.find(rate => rate.name === ElecRates.Day)
  }

  get elecNightRate() {
    return this.elecRates.find(rate => rate.name === ElecRates.Night)
  }

  get elecEvPeakRate() {
    return this.elecRates.find(rate => rate.name === ElecRates.EvPeak)
  }

  get elecEvOffPeakRate() {
    return this.elecRates.find(rate => rate.name === ElecRates.EvOffPeak)
  }

  get elecUnitRateStandard() {
    return this.elecStandardRate?.costPerKwhInPence
  }

  get annualElecDayUsage() {
    if (!this.isEco7) return this.annualElecUsage
    return this.elecDayRate?.eco7ElecConsumptionPerYearInKwh
  }

  get annualElecNightUsage() {
    return this.elecNightRate?.eco7ElecConsumptionPerYearInKwh
  }

  get elecUnitRateDay() {
    if (!this.isEco7) return this.annualElecUsage
    return this.elecDayRate?.costPerKwhInPence
  }

  get elecUnitRateNight() {
    return this.elecNightRate?.costPerKwhInPence
  }

  get isTrackerTariff() {
    return [this.elecProductCode, this.gasProductCode].some(code =>
      code?.includes(SO_GREEN_TRACKER_TARIFF_CODE)
    )
  }

  get tariffDurationFormatted() {
    if (this.isTrackerTariff) {
      return "Tracker Tariff"
    }

    if (this.isVariable) return "Variable Tariff"

    if (this.durationMonths % 12 === 0) {
      const durationInYears = ["One", "Two", "Three", "Four", "Five", "Six"][
        Math.trunc(this.durationMonths / 12) - 1
      ]
      return `${durationInYears} Year Tariff`
    }
    return `${this.durationMonths} Month Tariff`
  }

  get endDate() {
    return this.durationMonths
      ? dayjs(this.startDate).add(this.durationMonths, "months")
      : null
  }

  getAdjustedMonthlyCost(adjustmentFactor) {
    const price = this.monthlyCost() * adjustmentFactor
    return price % 1 !== 0 ? price.toFixed(2) : String(price)
  }

  get soFlexTariff() {
    return this.elecProductCode?.includes("SOOC") ?? false
  }
}

export default TariffQuote
